import { Component, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';

@Component({
  selector: 'app-unified-insurance',
  imports: [CommonModule],
  standalone: true,
  templateUrl: './unified-insurance.component.html',
  styleUrl: './unified-insurance.component.scss'
})
export class UnifiedInsuranceComponent implements OnInit, OnDestroy {
  private jsonLdScript: any;
  constructor(private router: Router, private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }
  nav(url: any) {
    console.log(url);
    this.router.navigateByUrl(url)
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route: { data: any; }) => route.data)
    )
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
injectSchemaMarkup(): void {
    const insuranceBillingSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/unified-system-insurance",
          "url": "https://www.kalpitatechnologies.com/kalpita-case-studies/unified-system-insurance",
          "name": "Unified System for Insurance: Enhancing Efficiency with Automated Billing – Case Study",
          "description": "Case study on how Kalpita Technologies developed a robust automated billing system with Azure cloud functionalities for a top-tier insurance company specializing in property, liability, and flood management.",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [
              ".unifiedInsure-background h1",
              ".sub-heading",
              ".para-text"
            ]
          },
          "datePublished": "2025-01-28",
          "dateModified": "2025-01-28",
          "isPartOf": {
            "@type": "WebSite",
            "@id": "https://www.kalpitatechnologies.com/#website",
            "name": "Kalpita Technologies",
            "url": "https://www.kalpitatechnologies.com/"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/unified-system-insurance#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "item": {
                "@type": "WebPage",
                "@id": "https://www.kalpitatechnologies.com/",
                "url": "https://www.kalpitatechnologies.com/",
                "name": "Home"
              }
            },
            {
              "@type": "ListItem",
              "position": 2,
              "item": {
                "@type": "WebPage",
                "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/case-study",
                "url": "https://www.kalpitatechnologies.com/kalpita-case-studies/case-study",
                "name": "Case Studies"
              }
            },
            {
              "@type": "ListItem",
              "position": 3,
              "item": {
                "@type": "WebPage",
                "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/unified-system-insurance",
                "url": "https://www.kalpitatechnologies.com/kalpita-case-studies/unified-system-insurance",
                "name": "Unified System for Insurance"
              }
            }
          ]
        },
        {
          "@type": "Article",
          "headline": "Unified System for Insurance: Enhancing Efficiency with Automated Billing",
          "alternativeHeadline": "Automated Billing System with Azure Cloud for Insurance – Hazard Case Study",
          "articleBody": "Kalpita Technologies developed a robust and sophisticated automated billing system with Azure cloud functionalities for a top-tier insurance company specializing in property, liability, and flood management...",
          "keywords": [
            "insurance billing automation", "automated billing system", "Azure Functions billing", "insurance technology", "billing status management", "Azure cloud billing", "C# billing system"
          ],
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": {
              "@type": "ImageObject",
              "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png"
            }
          },
          "datePublished": "2025-01-28",
          "image": "https://www.kalpitatechnologies.com/assets/resources/casestudies/unifiedsystem-insurance/client-profile.png"
        },
        {
          "@type": "HowTo",
          "name": "How to Build an Automated Billing System for Insurance with Azure",
          "description": "Step-by-step approach Kalpita Technologies used to transform manual insurance billing into an automated, cloud-powered system.",
          "step": [
            { "@type": "HowToStep", "name": "Audit", "text": "Identify pain points in the manual billing system including inaccuracies and scalability issues." },
            { "@type": "HowToStep", "name": "Design", "text": "Develop comprehensive logic supporting multiple frequencies using C# and PL/SQL." },
            { "@type": "HowToStep", "name": "Deploy", "text": "Deploy Azure Functions with Timer Triggers to automate generation in U.S. Central Time." },
            { "@type": "HowToStep", "name": "Build UI", "text": "Create an intuitive Angular dashboard with role-based access for admins and lenders." },
            { "@type": "HowToStep", "name": "Enable Alerts", "text": "Implement real-time updates and proactive alerts for important billing events." }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What results did the automated billing system achieve?",
              "acceptedAnswer": { "@type": "Answer", "text": "The system achieved improved accuracy and efficiency, ensured timely billing with a 100% accuracy target, and boosted customer engagement." }
            },
            {
              "@type": "Question",
              "name": "How does it use Azure Functions?",
              "acceptedAnswer": { "@type": "Answer", "text": "It uses Timer Triggers to automate bill distribution on the first day of each month, with error handling via Application Insights." }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png"
          }
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://www.kalpitatechnologies.com/#localbusiness",
          "name": "Kalpita Technologies — Austin Office",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
            "addressLocality": "Austin", "addressRegion": "TX", "postalCode": "78746", "addressCountry": "US"
          },
          "telephone": "+1-512-240-2484"
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(insuranceBillingSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
}
